import * as React from "react";
import { motion, AnimatePresence } from "motion/react";
import { cn } from "../lib/utils";

export interface ProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  id: string | number;
  image: string;
  tag: string;
  logo: string;
  title?: string;
  description?: string;
}

const ProductModal = ({
  isOpen,
  onClose,
  id,
  image,
  tag,
  logo,
  title,
  description,
}: ProductModalProps) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-[1392px] mx-2 md:mx-4"
          >
            <motion.div
              className="relative flex flex-col md:flex-row rounded-[20px] overflow-hidden"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            >
              {/* Lewa strona - zdjęcie */}
              <motion.div className="w-full md:w-1/2 relative z-10">
                <motion.div
                  layoutId={`product-image-${id}`}
                  className="w-full h-[50vh] md:h-[90dvh] max-h-[400px] md:max-h-[766px] bg-cover bg-center rounded-t-[20px] md:rounded-l-[20px] md:rounded-t-none relative overflow-hidden"
                  style={{ backgroundImage: `url(${image})` }}
                >
                  {/* Tag */}
                  <div className="absolute top-0 left-0 z-10 flex justify-center items-center pr-[7px] pb-[7px] bg-white rounded-br-[20px] invert-product">
                    <span className="bg-off-white text-red-500 px-4 md:px-6 py-2.5 md:py-3.5 rounded-[20px] button-large text-xs md:text-sm">
                      {tag}
                    </span>
                  </div>

                  {/* Logo */}
                  <img
                    src={logo}
                    alt={title}
                    className="absolute bottom-2 md:bottom-4 left-2 md:left-4 z-10 w-16 h-16 md:w-auto md:h-auto"
                  />
                </motion.div>
              </motion.div>

              {/* Prawa strona - treść */}
              <motion.div
                initial={{ x: -100, scale: 0.8, opacity: 0 }}
                animate={{ x: 0, scale: 1, opacity: 1 }}
                exit={{ x: -100, scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="w-full md:w-1/2 p-4 md:p-8 overflow-y-auto rounded-b-[20px] md:rounded-r-[20px] md:rounded-b-none relative md:-ml-4 z-0 h-[50vh] md:h-[90dvh] max-h-[400px] md:max-h-[766px] bg-white"
              >
                {/* Przycisk zamknięcia */}
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ delay: 0.2 }}
                  onClick={onClose}
                  className="absolute top-2 md:top-4 right-2 md:right-4 z-10 bg-white rounded-full p-1.5 md:p-2 hover:bg-gray-100 transition-colors shadow-md"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-4 h-4 md:w-6 md:h-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </motion.button>

                {title && (
                  <motion.h3
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="heading-3 mb-4 text-xl md:text-2xl lg:text-3xl xl:text-4xl"
                  >
                    {title}
                  </motion.h3>
                )}

                {description && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="body-large text-sm md:text-base"
                    dangerouslySetInnerHTML={{ __html: description }}
                  />
                )}
              </motion.div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export { ProductModal };
